import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import { LightRatingQuestionsData, lightRatingQuestionsResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

import QuestionGroup, { QuestionType } from '../inputs/QuestionGroup';
import Text from '../Text';

interface NpoLightRatingQuestionsFormProps {
  initialData:
    | {
        id: string;
        answer: string | null;
      }[]
    | undefined;
  questionOptions: QuestionType[];
  isLoading: boolean;
  showBackButton?: boolean;
  showSaveAndContinueButton?: boolean;
  submitLabel?: string;
  onSubmitTapped: (data: LightRatingQuestionsData) => void;
}

const NpoLightRatingQuestionsForm = ({
  initialData,
  questionOptions,
  isLoading,
  showBackButton = true,
  showSaveAndContinueButton = true,
  submitLabel = i18n.t('mix.next'),
  onSubmitTapped
}: NpoLightRatingQuestionsFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount }
  } = useForm<LightRatingQuestionsData>({
    defaultValues: initialData ? { questions: initialData } : { questions: [] },
    mode: 'onChange',
    resolver: lightRatingQuestionsResolver
  });

  const onSubmit: SubmitHandler<LightRatingQuestionsData> = async (data) => {
    onSubmitTapped(data);
  };

  return (
    <FormWrapper>
      <View style={styles.container}>
        <View style={styles.fields}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.light_rating_questions.title')}
            infoButtonLabel={i18n.t('sign_in.nonProfit.light_rating_questions.info')}>
            {questionOptions.length === 0 ? (
              <Text>{i18n.t('sign_in.light_rating_questions.no_questions')}</Text>
            ) : (
              <QuestionGroup control={control} name="questions" questionOptions={questionOptions} />
            )}
          </OnboardingSection>
        </View>

        <View style={styles.buttonsContainer}>
          <View style={styles.nextBackContainer}>
            {showBackButton && router.canGoBack() && (
              <BrandedButton disabled={isLoading} style={styles.flexOne} onPress={router.back}>
                {capitalize(i18n.t('mix.back'))}
              </BrandedButton>
            )}
            <BrandedButton
              style={styles.flexOne}
              loading={isLoading}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {submitLabel}
            </BrandedButton>
          </View>
          {showSaveAndContinueButton && <SaveAndContinueLaterButton disabled={isLoading} />}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  fields: {
    flexDirection: 'column',
    gap: 25
  },

  flexOne: {
    flex: 1
  },

  halfWidth: {
    width: '50%'
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column',
    gap: 10
  }
});

export default NpoLightRatingQuestionsForm;
